import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, CheckCircle2, AlertTriangle, Loader2, UserPlus, ClipboardList, PenLine, Check } from 'lucide-react';

export const TC_API = `${process.env.REACT_APP_BACKEND_URL}/api/training-cards`;
export const fmtD = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

// GET (or POST with a body) a PDF and open it in a new tab.
export const openPdf = async (url, body) => {
  try {
    const r = body ? await axios.post(url, body, { responseType: 'blob' }) : await axios.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(blobUrl, '_blank');
  } catch (e) { toast.error('Could not build the PDF'); }
};

// Someone turned up who wasn't booked → pick them from the employee list.
export function WalkInPicker({ pool, taken, onAdd, placeholder = 'Add a walk-in…', testid = 'tc-walkin', className = '' }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const opts = (pool || []).filter((p) => !taken.has(p.employee_id) && (!q || p.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 8);
  return (
    <div className={`relative ${className}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-lg px-2 py-1">
        <UserPlus className="w-3.5 h-3.5 text-emerald-700" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder} className="bg-transparent text-xs text-slate-900 w-32 sm:w-44 focus:outline-none" data-testid={`${testid}-input`} />
      </div>
      {open && opts.length > 0 && (
        <div className="absolute right-0 z-20 mt-1 w-64 max-h-60 overflow-y-auto bg-white border border-slate-300 rounded-lg shadow-2xl" data-testid={`${testid}-list`}>
          {opts.map((p) => (
            <button key={p.employee_id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onAdd(p); setQ(''); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-50 flex justify-between" data-testid={`${testid}-opt-${p.employee_id}`}>
              {p.name} <span className="text-[10px] text-slate-500 uppercase">{p.division || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Rows = people, columns = ticked courses. Everyone × everything is on by default.
// Desktop: a table. Phone: one card per person with course chips — same state either way.
export function AttendanceGrid({ attendees, courses, att, setAtt, pool, onAddWalkIn, signed = {}, onSign }) {
  const cols = courses.filter((c) => c.checked);
  const toggleAll = (on) => setAtt(Object.fromEntries(attendees.map((a) => [a.employee_id, { attended: on, topics: on ? new Set(cols.map((c) => c.topic)) : new Set() }])));
  const togglePerson = (id) => setAtt((p) => { const cur = p[id] || { attended: false }; return { ...p, [id]: { attended: !cur.attended, topics: !cur.attended ? new Set(cols.map((c) => c.topic)) : new Set() } }; });
  const toggleCell = (id, t) => setAtt((p) => { const cur = p[id] || { topics: new Set() }; const s = new Set(cur.topics); s.has(t) ? s.delete(t) : s.add(t); return { ...p, [id]: { attended: s.size > 0, topics: s } }; });
  const total = attendees.reduce((n, a) => n + cols.filter((c) => att[a.employee_id]?.topics.has(c.topic)).length, 0);
  const taken = new Set(attendees.map((a) => a.employee_id));
  const rowOf = (a) => att[a.employee_id] || { attended: false, topics: new Set() };
  const present = attendees.filter((a) => rowOf(a).attended).length;
  const signedCount = attendees.filter((a) => rowOf(a).attended && signed[a.employee_id]).length;
  const SignBtn = ({ a, big }) => onSign && rowOf(a).attended ? (
    <button type="button" onClick={() => onSign(a.employee_id)} title={signed[a.employee_id] ? 'Signed in — tap to re-sign' : 'Sign in on screen'} data-testid={`tc-sign-${a.employee_id}`}
      className={`shrink-0 inline-flex items-center gap-1 rounded-full border ${big ? 'px-2.5 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]'} ${signed[a.employee_id] ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-white border-slate-300 text-slate-700 hover:text-slate-900'}`}>
      {signed[a.employee_id] ? <><Check className="w-3 h-3" /> signed</> : <><PenLine className="w-3 h-3" /> sign</>}
    </button>
  ) : null;
  return (
    <div data-testid="tc-attendance">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-slate-900">3. Who was there — <span className="text-purple-800 font-normal" data-testid="tc-present-count">{present} of {attendees.length} present · {total} card{total !== 1 ? 's' : ''}</span></p>
        <div className="flex flex-wrap gap-1.5 items-center">
          {onSign && present > 0 && (
            <button onClick={() => onSign(null)} className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1" title="Pass the tablet around — everyone signs the attendance sheet on screen" data-testid="tc-pass-tablet">
              <PenLine className="w-3.5 h-3.5" /> Sign-in: pass the tablet <span className="font-mono opacity-80">{signedCount}/{present}</span>
            </button>
          )}
          {onAddWalkIn && <WalkInPicker pool={pool} taken={taken} onAdd={onAddWalkIn} />}
          <button onClick={() => toggleAll(true)} className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white" data-testid="tc-everyone-everything">Everyone did everything</button>
          <button onClick={() => toggleAll(false)} className="px-2.5 py-1 rounded-lg text-xs bg-white border border-slate-300 text-slate-700" data-testid="tc-clear-all">Clear</button>
        </div>
      </div>

      <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-white text-slate-700">
            <tr>
              <th className="text-left px-2 py-2 font-semibold">Attended</th>
              {cols.map((c) => <th key={c.topic} className="px-1 py-2 font-semibold text-center whitespace-nowrap" title={c.title}>{c.title.length > 18 ? `${c.title.slice(0, 17)}…` : c.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {attendees.map((a) => {
              const row = rowOf(a);
              return (
                <tr key={a.employee_id} className={`border-t border-slate-200 ${row.attended ? '' : 'opacity-50'}`} data-testid={`tc-row-${a.employee_id}`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <label className="inline-flex items-center gap-2 cursor-pointer text-slate-800">
                      <input type="checkbox" checked={row.attended} onChange={() => togglePerson(a.employee_id)} className="w-4 h-4 accent-purple-500" data-testid={`tc-attended-${a.employee_id}`} />
                      {a.name} <span className="text-[10px] text-slate-500 uppercase">{a.division || ''}</span>
                      {a.walk_in && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase">walk-in</span>}
                    </label>
                    <span className="ml-2 inline-block align-middle"><SignBtn a={a} /></span>
                  </td>
                  {cols.map((c) => (
                    <td key={c.topic} className="text-center px-1 py-1.5">
                      <input type="checkbox" checked={row.topics.has(c.topic)} onChange={() => toggleCell(a.employee_id, c.topic)} className="w-4 h-4 accent-emerald-500" data-testid={`tc-cell-${a.employee_id}-${c.topic}`} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2" data-testid="tc-attendance-cards">
        {attendees.map((a) => {
          const row = rowOf(a);
          return (
            <div key={a.employee_id} className={`rounded-xl border p-3 ${row.attended ? 'border-purple-200 bg-purple-50' : 'border-slate-200 bg-white opacity-70'}`} data-testid={`tc-card-${a.employee_id}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={row.attended} onChange={() => togglePerson(a.employee_id)} className="w-6 h-6 accent-purple-500" data-testid={`tc-attended-m-${a.employee_id}`} />
                <span className="text-base text-slate-900 font-semibold flex-1">{a.name}</span>
                {a.walk_in && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase">walk-in</span>}
                <span className="text-xs text-slate-500">{row.topics.size}/{cols.length}</span>
              </label>
              <div className="mt-1.5"><SignBtn a={a} big /></div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {cols.map((c) => {
                  const on = row.topics.has(c.topic);
                  return (
                    <button key={c.topic} type="button" onClick={() => toggleCell(a.employee_id, c.topic)} data-testid={`tc-chip-${a.employee_id}-${c.topic}`}
                      className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-300 text-slate-500 line-through'}`}>
                      {c.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// After Generate: live progress, then the summary + print.
export function BatchView({ batch, pdfUrl, signOffUrl }) {
  if (!batch) return null;
  const running = batch.status === 'running';
  const pct = batch.total ? Math.round(((batch.done + batch.failed) / batch.total) * 100) : 0;
  const people = {};
  (batch.cards || []).forEach((c) => { (people[c.employee_name] = people[c.employee_name] || []).push(c); });
  return (
    <div className="space-y-3" data-testid="tc-batch">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-slate-900 font-semibold flex items-center gap-2">
            {running ? <Loader2 className="w-4 h-4 animate-spin text-purple-700" /> : <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
            <span data-testid="tc-batch-status">{running ? `Making cards… ${batch.done + batch.failed} of ${batch.total}` : `${batch.done} card${batch.done !== 1 ? 's' : ''} issued`}</span>
            {batch.failed > 0 && <span className="text-red-700 text-xs">· {batch.failed} failed</span>}
            {batch.drive_errors > 0 && <span className="text-amber-700 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {batch.drive_errors} not filed to Drive</span>}
          </p>
          {!running && batch.done > 0 && (
            <div className="flex flex-wrap gap-2">
              {signOffUrl && (
                <button onClick={() => openPdf(signOffUrl)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-slate-100 text-slate-900 hover:bg-slate-200 flex items-center gap-1.5" title="Attendance sheet with a signature line per person — for the trainer's records" data-testid="tc-batch-signoff">
                  <ClipboardList className="w-4 h-4" /> Sign-off sheet
                </button>
              )}
              {pdfUrl && (
                <button onClick={() => openPdf(pdfUrl)} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white text-slate-900 hover:bg-slate-100 flex items-center gap-1.5" data-testid="tc-print-pdf">
                  <Printer className="w-4 h-4" /> Print all cards (PDF)
                </button>
              )}
            </div>
          )}
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-purple-500 transition-all" style={{ width: `${pct}%` }} /></div>
        <p className="text-[11px] text-slate-500 mt-1">Trained {fmtD(batch.training_date)} · signed by {batch.trainer_name} · each card is filed in the person's Drive safety file and counts in the compliance tracker right away.</p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(people).map(([name, cards]) => (
          <div key={name} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="tc-person-cards">
            <p className="text-sm text-slate-900 font-semibold">{name} <span className="text-xs text-slate-500 font-normal">· {cards.length}</span></p>
            <div className="flex flex-wrap gap-1 mt-1">
              {cards.map((c) => (
                <span key={c.id} title={c.error || `${c.card_no} · expires ${fmtD(c.expiry)}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${c.status === 'failed' ? 'border-red-300 text-red-700' : c.status === 'done' ? (c.drive_file_id ? 'border-emerald-200 text-emerald-800' : 'border-amber-200 text-amber-800') : 'border-slate-300 text-slate-500'}`}>
                  {c.title}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
